import { createSelector } from 'reselect'
import { Map, Set } from 'immutable'
import { hasConversationAccess } from 'app/modules/access/helpers'
import * as UserSelectors from 'app/modules/user/selectors'
import { currentInboxId } from 'app/modules/inbox/selectors'
import { unreadConversationIds } from 'app/modules/unread/selectors'
import { ActiveConversationStatuses } from 'constants/ConversationStatusConstants'
import { SortConversationsEnum } from 'constants/SortConversationsConstants'
import * as constants from './constants'
import * as actions from './actions'
import stringify from 'fast-json-stable-stringify'

export const root = (state) => state.get('conversations')

export const selectedConversations = (state) => root(state).get('selectedConversations')
export const isConversationSelected = (state, { conversationId }) => selectedConversations(state).has(conversationId)
export const selectedConversationsCount = (state) => selectedConversations(state).size
export const isSelectedConversationsLimitReached = (state) => selectedConversationsCount(state) === constants.MAX_SELECTED_CONVERSATIONS_COUNT

export const composeModalStatus = (state) => root(state).get('composeModalStatus').status
export const composeModalEndUserId = (state) => root(state).get('composeModalStatus').endUserId
export const composeModalEndUser = (state) => root(state).get('composeModalStatus').endUser
export const composeType = (state) => root(state).get('composeModalStatus').composeType
export const composeModalCompanyName = (state) => root(state).get('composeModalStatus').companyName
export const composeModalPage = (state) => root(state).get('composeModalStatus').page

const activeQueryByInboxId = (state) => root(state).get('activeQueryByInboxId')
const activeConversationByInboxId = (state) => root(state).get('activeConversationByInboxId')

export const byId = (state) => root(state).get('byId')

const paginatedConversationsByQuery = (state) => {
  return root(state).get('paginatedConversationsByQuery')
}
const paginatedConversationDataById = (state) => root(state).get('paginatedConversationDataById')

const endUserConversationsCounts = (state) => root(state).get('endUserConversationsCounts')

const paginatedConversations = (state, { query }) => {
  return paginatedConversationsByQuery(state).get(stringify(query), Map())
}

const makePaginatedConversations = () => createSelector(
  paginatedConversations,
  (paginatedConversations) => paginatedConversations)

export const getSuggestedArticles = state => root(state).get('suggestedArticles')

const globalAutoCloseConfiguration = state => root(state).get('globalAutoCloseConfiguration')
const globalAutoCloseConfigurationData = state => globalAutoCloseConfiguration(state).get('data')
const globalAutoCloseConfigurationStatus = state => globalAutoCloseConfiguration(state).get('status')

export const getGlobalAutoCloseConfiguration = state => globalAutoCloseConfigurationData(state)

export const isGlobalAutoCloseConfigurationLoading = state => {
  const status = globalAutoCloseConfigurationStatus(state).get('fetch')
  return status === constants.FETCH_GLOBAL_AUTO_CLOSE_CONFIGURATION_PENDING
}

export const isGlobalAutoCloseConfigurationLoaded = state => {
  const status = globalAutoCloseConfigurationStatus(state).get('fetch')
  return [
    constants.FETCH_GLOBAL_AUTO_CLOSE_CONFIGURATION_SUCCESS,
    constants.FETCH_GLOBAL_AUTO_CLOSE_CONFIGURATION_FAILURE,
  ].includes(status)
}

export const makePaginatedConversationData = () => createSelector(
  [
    paginatedConversationDataById,
    (state, { conversationId }) => conversationId,
  ],
  (paginatedConversationDataById, conversationId) =>
    paginatedConversationDataById.get(conversationId))

export const filteredPaginatedConversationDataById = createSelector(
  [
    paginatedConversationDataById,
    UserSelectors.currentUser,
  ],
  (paginatedConversationDataById, user) =>
    paginatedConversationDataById
      .filter(conversationData => {
        return hasConversationAccess(conversationData.participantIds, user)
      }))

export const makeFilteredPaginatedConversationDataById = () => createSelector(
  [
    paginatedConversationDataById,
    UserSelectors.currentUser,
  ],
  (paginatedConversationDataById, user) =>
    paginatedConversationDataById
      .filter(conversationData => {
        return hasConversationAccess(conversationData.participantIds, user)
      }))


export const activeQuery = createSelector(
  [
    currentInboxId,
    activeQueryByInboxId,
  ],
  (currentInboxId, activeQueryByInboxId) => activeQueryByInboxId.get(currentInboxId, Map()))

const parseParticipantFilter = (query) => {
  if (query.get('participantFilter') === '0') {
    return Set([0])
  }
  return Set(query.get('participantFilter')).map(id => parseInt(id))
}

const queryAppliesToConversation = (query, conversation) => {
  if (!query) {
    return false
  }

  if (query.get('statusFilter') != null) {
    if (!query.get('statusFilter').includes(conversation.status)) {
      return false
    }
  } else {
    if (!ActiveConversationStatuses.includes(conversation.status)) {
      return false
    }
  }

  if (query.get('participantFilter') != null) {
    const filteredParticipantIds = parseParticipantFilter(query)
    const matchesNoParticipants = filteredParticipantIds.has(0) && conversation.participantIds.size === 0
    const matchesSelectedParticipants = Set(conversation.participantIds).intersect(filteredParticipantIds).count() > 0
    if (!matchesNoParticipants && !matchesSelectedParticipants) {
      return false
    }
  }

  if (query.get('typeFilter') != null) {
    if (!query.get('typeFilter').includes(conversation.type)) {
      return false
    }
  }

  if (query.get('conversationTagFilter') != null) {
    if (query.get('conversationTagFilter').some(tagFilter => {
      return !(conversation.tags && conversation.tags.includes(parseInt(tagFilter)))
    })) {
      return false
    }
  }

  return true
}

export const makeConversations = () => {
  const filteredPaginatedConversationDataByIdSelector = makeFilteredPaginatedConversationDataById()
  const paginatedConversationsSelector = makePaginatedConversations()

  return createSelector(
    [
      filteredPaginatedConversationDataByIdSelector,
      paginatedConversationsSelector,
      currentInboxId,
      unreadConversationIds,
      activeConversationId,
      (state, { query }) => query,
      (state, { filterUnreadsOnly }) => filterUnreadsOnly,
    ],
    (
      filteredPaginatedConversationDataById,
      paginatedConversations,
      currentInboxId,
      unreadConversationIds,
      activeConversationId,
      query,
      filterUnreadsOnly,
    ) => {
      const filteredByQuery = filteredPaginatedConversationDataById
        .filter(conversationData => {
          return (conversationData.inboxId === currentInboxId)
            && queryAppliesToConversation(query, conversationData)
        })
        .filter(conversation => {
          if (filterUnreadsOnly) {
            return unreadConversationIds.includes(conversation.id) || conversation.id === activeConversationId
          }

          return true
        })

      const sortedConversations = filteredByQuery
        .sortBy(conversationData => {
          if (query.get('sortBy') === SortConversationsEnum.OLDEST.value) {
            return conversationData.updatedAt
          }

          return -conversationData.updatedAt
        })

      const filteredByPaginationCursor = sortedConversations
        .filter(conversationData => {
          if (paginatedConversations) {
            const cursor = paginatedConversations.get('cursor')
            const hasMore = paginatedConversations.get('hasMore')

            if (hasMore && cursor) {
              if (query.get('sortBy') === SortConversationsEnum.OLDEST.value) {
                return conversationData.updatedAt <= cursor
              }

              return conversationData.updatedAt >= cursor
            }
          }

          return true
        })

      return filteredByPaginationCursor
    })
}

export const makeActiveConversations = () => {
  const conversationsSelector = makeConversations()

  return createSelector(
    [
      activeQuery,
      (state) => state,
    ],
    (activeQuery, state) =>
      conversationsSelector(state, { query: activeQuery }))
}

export const makeConversationsCount = () => {
  const conversationsSelector = makeConversations()

  return createSelector(conversationsSelector,
    (conversations) => conversations.count())
}

export const hasMoreConversations = createSelector(paginatedConversations,
  (paginatedConversations) => paginatedConversations.get('hasMore', false))

export const nextCursor = createSelector(paginatedConversations,
  (paginatedConversations) => paginatedConversations.get('cursor', 0))

export const conversationHasLoaded = createSelector(paginatedConversations,
  (paginatedConversations) => paginatedConversations.get('loaded', false))

export const conversationIsLoading = createSelector(paginatedConversations,
  (paginatedConversations) => paginatedConversations.get('loading', false))

export const makeConversationSelector = () => createSelector(
  [
    byId,
    (state, props) => props.conversationId
  ],
  (byId, conversationId) => byId.getIn([conversationId, 'data'])
)
export const makeConversationLoadedSelector = () => createSelector(
  [
    byId,
    (state, props) => props.conversationId
  ],
  (byId, conversationId) => {
    return [
      constants.RECEIVE_CONVERSATION,
      constants.FETCH_CONVERSATION_FAILED,
    ].includes(byId.getIn([conversationId, 'status']))
  }
)

export const makeFetchConversationFailedSelector = () => createSelector(
  [
    byId,
    (state, props) => props.conversationId
  ],
  (byId, conversationId) => {
    return byId.getIn([conversationId, 'status']) === constants.FETCH_CONVERSATION_FAILED
  }
)

export const activeConversationContactTabIndex = (state) => root(state).get('activeConversationContactTabIndex')

export const activeConversationId = createSelector(
  [
    currentInboxId,
    activeConversationByInboxId,
  ],
  (currentInboxId, activeConversationByInboxId) => activeConversationByInboxId.get(currentInboxId) || null)

export const activeConversation = createSelector(
  [
    activeConversationId,
    byId,
  ],
  (activeConversationId, byId) => byId.getIn([activeConversationId, 'data']))

export const endUserConversationsCountFetched = (state, props) => endUserConversationsCounts(state).has(props.endUser.id)
export const endUserConversationsCount = (state, props) => endUserConversationsCounts(state).get(props.endUser.id)

export const conversationCountForEndUser = (state, { endUserId }) => endUserConversationsCounts(state).get(endUserId, { all: 0 }).all

export const playbookIdByConversationId = (state) => root(state).get('playbookIdByConversationId')

export const getInfluencingPlaybookIdForActiveConversation = (state) => {
  const conversationSelector = makeConversationSelector()
  const conversationFromState = conversationSelector(state, { conversationId: activeConversationId(state) })
  return conversationFromState && conversationFromState.getIn(['context', 'influencingCampaignId'], 0)
}

export const getPlaybookIdForActiveConversation = createSelector(
    activeConversationId,
  playbookIdByConversationId,
    (activeConversationId, playbookIdByConversationId) => playbookIdByConversationId.get(activeConversationId)
)

export const makeConversation = () => (state, props) => {
  return byId(state).getIn([props.conversationId, 'data'])
}

export const conversationTags = (state, props) => {
  return byId(state).getIn([props.conversationId, 'data', 'tags'])
}

export const endUserIdsOfActiveConversations = createSelector(
  filteredPaginatedConversationDataById,
  activeConversation,
  (filteredPaginatedConversationDataById, activeConversation) => {
    const activeConversations = activeConversation
      ? filteredPaginatedConversationDataById.set(activeConversation.id, activeConversation)
      : filteredPaginatedConversationDataById
    return Set(activeConversations.map(conversation => conversation.endUserId).valueSeq())
  }
)

export const getRoutingAuditLog = (state, routingGroupId) => { return root(state).getIn(['routingAuditLogById', routingGroupId])}
export const getRoutingAuditLogLoaded = (state, routingGroupId) => root(state).getIn(['routingAuditLogStatusById', routingGroupId]) === actions.fetchRoutingAuditLogSuccess.toString()
export const getRoutingAuditLogNeverLoaded = (state, routingGroupId) => root(state).getIn(['routingAuditLogStatusById', routingGroupId]) === undefined
export const routingAuditLogHasRules = (state, routingGroupId) => {
  return getRoutingAuditLogLoaded(state, routingGroupId) && getRoutingAuditLog(state, routingGroupId).some(entry => (entry.routingEntryType === 'RULE_ROUTE'))
}

export const makeParticipantIdsByConversationId = () => createSelector(
  [
    filteredPaginatedConversationDataById,
    (state, { conversationIds }) => conversationIds,
  ],
  (filteredPaginatedConversationDataById, conversationIds) => {
    const participantIdsByConversationId = conversationIds.reduce((accum, conversationId) => {
      const conversation = filteredPaginatedConversationDataById.get(conversationId)

      if (!conversation) {
        return accum
      }

      return accum.set(conversationId, conversation.participantIds)
    }, Map())

    return participantIdsByConversationId
  })

export const makeConversationStatusByConversationId = () => createSelector(
  [
    filteredPaginatedConversationDataById,
    (state, { conversationIds }) => conversationIds,
  ],
  (filteredPaginatedConversationDataById, conversationIds) => conversationIds.reduce((accum, conversationId) => {
    const conversation = filteredPaginatedConversationDataById.get(conversationId)

    if (!conversation) {
      return accum
    }

    return accum.set(conversationId, conversation.status)
  }, Map()))



// WEBPACK FOOTER //
// ./src/scripts/app/modules/conversation/selectors.js